import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Home as HomeIcon,
  Article as ArticleIcon,
  Menu as MenuIcon,
  NavigateNext as NavigateNextIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { BLOG_CATEGORIES } from '../../types/blog';
import { categories as usedCategories } from '../../data/blog.generated';

interface BlogLayoutProps {
  children: React.ReactNode;
  title: string;
  description?: string;
  breadcrumbs?: { label: string; path?: string }[];
  showSearch?: boolean;
  onSearch?: (query: string) => void;
  searchValue?: string;
}

// Only show categories that have at least one blog post
const NAV_ITEMS = [
  { label: 'All Posts', path: '/blog', icon: <HomeIcon /> },
  ...BLOG_CATEGORIES
    .filter(cat => usedCategories.includes(cat.value))
    .map(cat => ({
      label: cat.label,
      path: `/blog/category/${cat.value}`,
      icon: <ArticleIcon sx={{ color: cat.color }} />,
    })),
];

export const BlogLayout: React.FC<BlogLayoutProps> = ({
  children,
  title,
  description,
  breadcrumbs = [],
  showSearch = true,
  onSearch,
  searchValue = '',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [localSearch, setLocalSearch] = React.useState(searchValue);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return;
    }
    e.preventDefault();
    navigate(path);
    window.scrollTo(0, 0);
    if (isMobile) setMobileOpen(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(e.target.value);
    onSearch?.(e.target.value);
  };

  const isActivePath = (path: string) => {
    if (path === '/blog') {
      return location.pathname === '/blog';
    }
    return location.pathname.startsWith(path);
  };

  const navContent = (
    <Box>
      {showSearch && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search posts..."
            value={localSearch}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
      <List sx={{ py: 2 }}>
        {NAV_ITEMS.map((item) => (
          <ListItemButton
            key={item.path}
            component="a"
            href={item.path}
            selected={isActivePath(item.path)}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleNavClick(e, item.path)}
            sx={{
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                },
                '&:hover': {
                  bgcolor: 'primary.main',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: isActivePath(item.path) ? 600 : 400,
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 200px)' }}>
      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" color="primary">
              Blog
            </Typography>
          </Box>
          {navContent}
        </Drawer>
      )}

      {/* Desktop sidebar */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            width: 260,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" color="primary">
              Blog
            </Typography>
          </Box>
          {navContent}
        </Paper>
      )}

      {/* Main content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          {/* Mobile menu button */}
          {isMobile && (
            <Box sx={{ mb: 2 }}>
              <IconButton onClick={handleDrawerToggle} edge="start">
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{ mb: 2 }}
            >
              <Link
                component={RouterLink}
                to="/blog"
                color="inherit"
                underline="hover"
              >
                Blog
              </Link>
              {breadcrumbs.map((crumb, index) =>
                crumb.path && index < breadcrumbs.length - 1 ? (
                  <Link
                    key={crumb.label}
                    component={RouterLink}
                    to={crumb.path}
                    color="inherit"
                    underline="hover"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <Typography key={crumb.label} color="text.primary">
                    {crumb.label}
                  </Typography>
                )
              )}
            </Breadcrumbs>
          )}

          {/* Page header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              fontWeight="bold"
            >
              {title}
            </Typography>
            {description && (
              <Typography variant="body1" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>

          {/* Page content */}
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default BlogLayout;
